import { useRef, useState, useEffect, ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { PIShell, Drill, PIInsight, PracticeLadder } from "@/components/pi/PIShell";
import { KeyPicker } from "@/components/KeyPicker";
import { KeyProvider, useKeyOrDefault } from "@/theory/key";
import { midiToPitchName, tonicMidiForPc } from "@/theory/melody";
import { kickAudio } from "@/audio/piano";
import { playRhythm, setRhythmBpm, RhythmHandle } from "@/audio/rhythm";

/**
 * Piano Intermediate · Lesson 23 — I Will Survive · The Descending Fifths Cycle.
 *
 * Bespoke visual: FifthsCycleWheel — an SVG circle with 8 wedges arranged
 * clockwise from 12 o'clock: Am → Dm → G → C → F → Bm7♭5 → E7 → Am.
 * The active wedge glows amber during playback; a cursor dot tracks the position.
 */

const CYCLE = [
  { name: "Am",    quality: "minor",            color: "#f59e0b" },
  { name: "Dm",    quality: "minor",            color: "#06b6d4" },
  { name: "G",     quality: "major",            color: "#10b981" },
  { name: "C",     quality: "major",            color: "#8b5cf6" },
  { name: "F",     quality: "major",            color: "#ec4899" },
  { name: "Bm7♭5", quality: "half-diminished",  color: "#f97316" },
  { name: "E7",    quality: "dominant seventh", color: "#ef4444" },
  { name: "Am",    quality: "resolve",          color: "#f59e0b" },
];

// Semitone offsets from tonicMidi (A natural minor tonic) for each chord's upper voices.
// All voicings are upper-register chord stabs — no separate bass.
const CHORD_OFFSETS: number[][] = [
  [0, 3, 7],        // Am:    A C E
  [5, 8, 12],       // Dm:    D F A'
  [-2, 2, 5],       // G:     G B D  (G below tonic A, open voicing)
  [3, 7, 10],       // C:     C E G
  [8, 12, 15],      // F:     F' A' C''
  [2, 5, 8, 12],    // Bm7♭5: B D F A
  [7, 11, 14, 17],  // E7:    E G# B D  (G# = +11 = harmonic minor raised 7th)
  [0, 3, 7],        // Am (resolve): same as first
];

export default function PianoIntermediate23() {
  const { lesson, course, accent } = findLesson("piano-intermediate", "23");
  return (
    <LessonShell
      course={course}
      courseSlug="piano-intermediate"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <KeyProvider defaultTonic="A" availableQualities={["natural-minor"]}>
        <PIShell
          title="I Will Survive — The Descending Fifths Cycle"
          exerciseGoal="Eight chords, each a fifth lower than the last — six perfect, one diminished. Practise the whole long sequence and you'll get really confident with it."
          bpm={100}
          difficulty="Intermediate"
        >
          <Body />
        </PIShell>
      </KeyProvider>
    </LessonShell>
  );
}

function Body() {
  const k = useKeyOrDefault();
  const tonicMidi = tonicMidiForPc(k.tonicPc);

  const [bpm, setBpm] = useState(100);
  const [running, setRunning] = useState(false);
  const [activeChord, setActiveChord] = useState(-1);
  const handleRef = useRef<RhythmHandle | null>(null);

  useEffect(() => {
    if (running) setRhythmBpm(bpm, 0.05);
  }, [bpm, running]);

  const stop = () => {
    handleRef.current?.stop();
    handleRef.current = null;
    setRunning(false);
    setActiveChord(-1);
  };

  const play = async () => {
    kickAudio();
    stop();
    setRunning(true);

    // 8 chords × 4 beats each = 32 steps at subdivide=1 (quarter notes)
    const events = CHORD_OFFSETS.map((offsets, i) => ({
      beat: i * 4,
      notes: offsets.map((off) => midiToPitchName(tonicMidi + off, k.useFlats)),
      durationSteps: 3,
      velocity: 0.82,
    }));

    handleRef.current = await playRhythm(events, {
      bpm,
      subdivide: 1,
      totalSteps: 32,
      loop: true,
      click: false,
      onStep: (s) => setActiveChord(Math.floor(s / 4)),
    });
  };

  return (
    <>
      <StudyDeck>
        <KeyPicker />
        <p className="text-[12.5px] text-cyan-300/70">
          The lesson is rooted on A natural minor. The key picker shifts the tonal centre
          but the cycle structure (descending fifths) remains the same in all keys.
        </p>
      </StudyDeck>

      <FadeIn>
        <Drill number="1" label="The concept" title="The mother of all chord progressions">
          <p>
            Every chord in this progression is a <strong>fifth lower</strong> than the one
            before it. Am → Dm is a fifth down. Dm → G is a fifth down. G → C, C → F,
            F → Bm7♭5, Bm7♭5 → E7, E7 → Am. The ear keeps getting pulled forward: the
            harmonic gravity never stops.
          </p>
          <p>
            Six of those seven steps are <strong>perfect</strong> fifths. One is not.
            Count from <strong>F</strong> down to <strong>B</strong>: F–E–D–C–B is still a
            fifth by letter name, but it spans six semitones instead of seven — a
            <strong> diminished fifth</strong>, the tritone. Staying inside one key makes
            this unavoidable; every diatonic cycle of fifths has exactly one such link.
            And it is not a flaw, it is the explanation: the diminished fifth is precisely
            why the chord waiting on B is <strong>Bm7♭5</strong>, the half-diminished one,
            rather than another plain minor chord. The odd step and the odd chord are the
            same fact seen twice.
          </p>
          <PIQuote attribution="Jason · lesson video">
            "You can build all the popular ones, the tinier ones, the one five six fours, the one
            four fives, pop stuff, rock stuff within this huge wheel of seven chords."
          </PIQuote>
          <p>
            That's why Jason calls it the mother of all chord progressions. It is the
            progression of Gloria Gaynor's <em>I Will Survive</em>, and he points to jazz
            standards too — <em>Fly Me to the Moon</em> has other chords as well, but at its core
            it is this cycle.
          </p>
          <PIInsight>
            <p>
              Today we tend to think of chord progressions as two chords, or four at most. This
              one is long — seven or eight chords — and that length is what gives it its endless
              sound. Practise the whole sequence until it is automatic.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="2" label="The wheel" title="See and hear the cycle">
          <p>
            Each wedge is one chord. The amber glow follows the active chord during playback.
            Watch the cursor dot travel clockwise — that journey is the cycle completing itself,
            arriving back where it started.
          </p>

          <FifthsCycleWheel activeChord={activeChord} />

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              onClick={play}
              disabled={running}
              className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-zinc-900 hover:bg-amber-300 disabled:opacity-50"
            >
              ▶ Loop the cycle
            </button>
            {running && (
              <button
                onClick={stop}
                className="rounded-lg border border-rose-400/60 bg-rose-500/10 px-3 py-2 text-sm font-semibold text-rose-200 hover:bg-rose-500/20"
              >
                ■ Stop
              </button>
            )}
            <label className="flex items-center gap-2 text-[12px] text-cyan-200">
              <span className="font-mono uppercase tracking-wider text-cyan-300/80">Tempo</span>
              <input
                type="range" min={80} max={140} step={1} value={bpm}
                onChange={(e) => setBpm(Number(e.target.value))}
                className="h-1 w-32 accent-amber-400"
              />
              <span className="w-10 font-mono text-[11px] text-cyan-100">{bpm}</span>
            </label>
          </div>

          {activeChord >= 0 && (
            <motion.div
              key={activeChord}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 flex items-baseline gap-3 rounded-xl border border-amber-400/40 bg-amber-500/10 px-4 py-2"
            >
              <span className="text-xl font-bold text-amber-200">{CYCLE[activeChord].name}</span>
              <span className="text-[13px] text-cyan-300/80">{CYCLE[activeChord].quality}</span>
            </motion.div>
          )}
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="3" label="The turnaround: E7" title="The dominant that pulls everything home">
          <p>
            In the key, the chord on E would be E minor. The song plays{" "}
            <strong>E major</strong> instead — or E7, the{" "}
            <strong>dominant seventh of Am</strong> — because it is a much stronger resolution to
            the minor than E minor.
          </p>
          <p className="mt-2 text-[14px] text-cyan-50/95">
            E7 contains a <strong>G♯</strong> — a note that does not belong to A natural minor.
            It is borrowed from A <em>harmonic</em> minor, where the 7th scale degree is raised
            by a semitone precisely to create this pull. That raised note is a{" "}
            <strong>leading tone</strong>: it sits one half-step below the tonic A and desperately
            wants to resolve upward.
          </p>
          <div className="my-3 rounded-2xl border border-rose-400/30 bg-rose-900/20 px-4 py-3">
            <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.22em] text-rose-300">
              E7 voicing
            </div>
            <div className="flex flex-wrap gap-3">
              {["E", "G♯", "B", "D"].map((note, i) => (
                <div
                  key={i}
                  className={`flex flex-col items-center rounded-lg px-3 py-2 ${
                    i === 1
                      ? "border border-rose-400/60 bg-rose-500/20"
                      : "border border-zinc-600 bg-zinc-800"
                  }`}
                >
                  <span className="font-mono text-base font-bold text-white">{note}</span>
                  <span className="text-[10px] text-cyan-300/70">
                    {["root", "3rd (raised 7th of A minor)", "5th", "♭7"][i]}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-2 text-[12px] text-rose-200/80">
              The G♯ sits one semitone below A — it is the note that pulls E7 to Am.
            </p>
          </div>
          <PIInsight>
            <p>
              The same move works in any minor key: replace the minor v chord with a major V (or
              V7) and the last step home gets a leading tone. Jason mentions slipping in more of
              these secondary dominants later to spice things up further.
            </p>
          </PIInsight>
        </Drill>
      </FadeIn>

      <FadeIn>
        <Drill number="4" label="Practice ladder" title="Sixteen minutes, four rungs">
          <PracticeLadder
            intro={
              <>
                Blocked chords first — no rhythm, just chord shapes and smooth transitions.
                The wheel needs to be automatic before you layer any groove on top of it.
              </>
            }
            rungs={[
              {
                title: "Blocked chords",
                minutes: 4,
                accent: "amber",
                detail: "Play each chord as a block for 4 beats, Am through E7 and back. Focus on smooth voice leading — keep common tones held where possible. No rhythm yet.",
              },
              {
                title: "Add bass root",
                minutes: 4,
                accent: "cyan",
                detail: "Left hand plays only the bass root of each chord (A, D, G, C, F, B, E). Right hand continues blocked chords. Feel the descending fifth motion in the bass.",
              },
              {
                title: "Voice leading inversions",
                minutes: 4,
                accent: "rose",
                detail: "Find the smoothest inversions. G can use B in the bass (1st inversion) to move smoothly from Dm's F. C can use E in the bass. The goal: eliminate jumps larger than a third.",
              },
              {
                title: "Sing the bass line while playing",
                minutes: 4,
                accent: "emerald",
                detail: "Sing A → D → G → C → F → B → E → A while your hands play the cycle. The descending-fifth motion becomes physical — you hear it, feel it, and sing it simultaneously.",
              },
            ]}
          />
        </Drill>
      </FadeIn>
    </>
  );
}

/* ── Bespoke visual: FifthsCycleWheel ──────────────────────────────── */
function FifthsCycleWheel({ activeChord }: { activeChord: number }) {
  const CX = 160;
  const CY = 160;
  const R_OUTER = 130;
  const R_INNER = 50;
  const R_TEXT = 95;
  const NUM_WEDGES = 8;

  const toRad = (deg: number) => (deg * Math.PI) / 180;

  // Wedge i: start angle = i*45 - 90 degrees (wedge 0 starts at top = 12 o'clock)
  const wedgePath = (i: number): string => {
    const startDeg = i * 45 - 90;
    const endDeg = (i + 1) * 45 - 90;
    const s = toRad(startDeg);
    const e = toRad(endDeg);

    const ox1 = CX + R_OUTER * Math.cos(s);
    const oy1 = CY + R_OUTER * Math.sin(s);
    const ox2 = CX + R_OUTER * Math.cos(e);
    const oy2 = CY + R_OUTER * Math.sin(e);
    const ix2 = CX + R_INNER * Math.cos(e);
    const iy2 = CY + R_INNER * Math.sin(e);
    const ix1 = CX + R_INNER * Math.cos(s);
    const iy1 = CY + R_INNER * Math.sin(s);

    return [
      `M ${ox1} ${oy1}`,
      `A ${R_OUTER} ${R_OUTER} 0 0 1 ${ox2} ${oy2}`,
      `L ${ix2} ${iy2}`,
      `A ${R_INNER} ${R_INNER} 0 0 0 ${ix1} ${iy1}`,
      "Z",
    ].join(" ");
  };

  const textPos = (i: number) => {
    const angleDeg = i * 45 + 22.5 - 90;
    const rad = toRad(angleDeg);
    return {
      x: CX + R_TEXT * Math.cos(rad),
      y: CY + R_TEXT * Math.sin(rad),
    };
  };

  const cursorPos = activeChord >= 0 ? textPos(activeChord) : null;

  return (
    <div className="my-4 flex flex-col items-center gap-4">
      <svg
        width={320}
        height={320}
        viewBox="0 0 320 320"
        aria-label="Descending fifths cycle wheel"
        className="overflow-visible"
      >
        {/* Dark background */}
        <circle cx={CX} cy={CY} r={R_OUTER + 8} fill="rgba(9,9,11,0.65)" />

        {/* Wedges */}
        {Array.from({ length: NUM_WEDGES }, (_, i) => {
          const chord = CYCLE[i];
          const isActive = i === activeChord;
          return (
            <g key={i}>
              <path
                d={wedgePath(i)}
                fill={isActive ? chord.color + "40" : chord.color + "18"}
                stroke={chord.color + (isActive ? "cc" : "44")}
                strokeWidth={isActive ? 2.5 : 1}
                style={{ transition: "fill 0.2s, stroke 0.2s, stroke-width 0.2s" }}
              />
              {/* Outer arc highlight for active */}
              {isActive && (
                <path
                  d={wedgePath(i)}
                  fill="none"
                  stroke={chord.color}
                  strokeWidth={3}
                  opacity={0.7}
                />
              )}
            </g>
          );
        })}

        {/* Centre hub */}
        <circle cx={CX} cy={CY} r={R_INNER} fill="rgba(9,9,11,0.92)" stroke="rgba(255,255,255,0.07)" strokeWidth={1} />
        <text x={CX} y={CY - 7} textAnchor="middle" fill="#fcd34d" fontSize="11" fontWeight="700" letterSpacing="1">
          5ths
        </text>
        <text x={CX} y={CY + 8} textAnchor="middle" fill="#67e8f9" fontSize="10" opacity="0.65">
          cycle
        </text>

        {/* Chord name labels in each wedge */}
        {Array.from({ length: NUM_WEDGES }, (_, i) => {
          const chord = CYCLE[i];
          const pos = textPos(i);
          const isActive = i === activeChord;
          const fontSize = chord.name.length > 3 ? "9.5" : "11.5";
          return (
            <text
              key={i}
              x={pos.x}
              y={pos.y + 4}
              textAnchor="middle"
              fontSize={fontSize}
              fontWeight={isActive ? "700" : "500"}
              fill={isActive ? "#fff" : chord.color + "bb"}
              style={{ transition: "fill 0.2s" }}
            >
              {chord.name}
            </text>
          );
        })}

        {/* Cursor dot tracks the active chord */}
        {cursorPos && (
          <motion.circle
            key={activeChord}
            cx={cursorPos.x}
            cy={cursorPos.y - 16}
            r={5}
            fill="#fbbf24"
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.14 }}
          />
        )}
      </svg>

      {/* Chord pill legend */}
      <div className="flex flex-wrap justify-center gap-1.5">
        {CYCLE.map((chord, i) => (
          <div
            key={i}
            className={`rounded-lg px-2.5 py-0.5 text-[11px] font-bold transition-all ${
              i === activeChord ? "scale-105" : ""
            }`}
            style={{
              background: chord.color + (i === activeChord ? "30" : "18"),
              border: `1px solid ${chord.color}${i === activeChord ? "88" : "44"}`,
              color: i === activeChord ? "#fff" : chord.color,
            }}
          >
            {chord.name}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Shared helpers ─────────────────────────────────────────────────── */
function FadeIn({ children }: { children: ReactNode }) {
  const reduced = useReducedMotion();
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: "easeOut" }}
    >
      {children}
    </motion.div>
  );
}

function StudyDeck({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="relative mb-4 rounded-2xl border border-cyan-400/30 bg-zinc-950/85 p-3">
      <button onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">✦ Study deck</div>
          <div className="text-[12px] text-cyan-300/70">Key picker · cycle reference</div>
        </div>
        <span className="text-[12px] font-mono text-cyan-300">{open ? "▴" : "▾"}</span>
      </button>
      <motion.div
        initial={false}
        animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        style={{ overflow: "hidden" }}
      >
        <div className="mt-3 space-y-3">{children}</div>
      </motion.div>
    </div>
  );
}

function PIQuote({ children, attribution }: { children: ReactNode; attribution?: string }) {
  return (
    <figure className="my-4 rounded-2xl border-l-4 border-amber-400/80 bg-zinc-900/60 px-5 py-4">
      <blockquote
        className="text-[17px] leading-relaxed text-cyan-50/95"
        style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: "italic" }}
      >
        {children}
      </blockquote>
      {attribution && (
        <figcaption className="mt-2 text-[11px] uppercase tracking-[0.18em] text-amber-300/80">
          — {attribution}
        </figcaption>
      )}
    </figure>
  );
}
